import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { AudioKeywordOrReasonSummaryActions } from '@modules/feedback/components/AudioType/AudioKeywordsAndReasonsSummary/AudioKeywordOrReasonSummaryActions';
import {
  getAudioSnippetSourceURL,
  getStartEndTimeFromAudioSummaryMetadata,
  isEmptyAudioSourceFeedback,
  isKeywordSummaryAudioBeingPlayed,
  trackAudioFeedbackEvents,
} from '@modules/feedback/components/AudioType/utils';
import { useUpdateSummaryFlag } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import useFeedbackListStore from '@modules/feedback/store/useFeedbackListStore';
import {
  getAudioSummaryFlagData,
  getSnippetShareUrl,
} from '@modules/feedback/utils';
import {
  SummaryFlag,
  SummaryMessageUnit,
  SummaryMetadata,
} from 'generated/graphql';
import {
  Feedback,
  FeedbackContentType,
  FeedbackLabel,
} from 'v2/core/entities/feedback';
import { useGetUser } from 'v2/core/services/user';
import { shallow } from 'zustand/shallow';

interface AudioSummaryActionsProps {
  feedback: Feedback;
  selectedLabels?: FeedbackLabel[];
  summary: SummaryMessageUnit;
  labelID?: string;
}

export const AudioSummaryActions = ({
  feedback,
  selectedLabels = [],
  summary,
  labelID,
}: AudioSummaryActionsProps) => {
  const {
    id: userID,
    tenant: { id: tenantID, name: tenantName, slug: tenantSlug },
  } = useGetUser();
  const flaggedSummaryRecords = useFeedbackOnFeedbackStore(
    ({ flaggedSummaryRecords }) => flaggedSummaryRecords,
  );

  const {
    msgIdx,
    keywordSummaryHash,
    uniqueKeywordSummaryHash,
    initialFlag,
    isSummaryFlagged,
  } = getAudioSummaryFlagData({
    feedback,
    summary,
    labelID,
    flaggedSummaryRecords,
  });

  const { updateSummaryFlag, isLoading: isFlagLoading } = useUpdateSummaryFlag({
    feedbackID: feedback.id,
    keywordSummaryHash: uniqueKeywordSummaryHash,
    summaryFlagType: SummaryFlag.Incorrect,
    initialFlag,
    keywordSummaryText: summary.text,
  });

  const { showSuccessNotification } = useNotificationContext();

  const { currentPlayingMsgIndex } = useFeedbackListStore(
    (state) => ({
      currentPlayingMsgIndex:
        state.feedbacks[feedback.id]?.currentPlayingMsgIndex ?? 0,
    }),
    shallow,
  );

  const onSummarySnippetShare = (startTime: number, endTime: number) => {
    const snippetShareUrl = getSnippetShareUrl({
      feedback,
      selectedLabels,
      startTime,
      endTime,
      tenantSlug,
    });

    navigator.clipboard.writeText(snippetShareUrl);
    showSuccessNotification({
      contentComponent: `Snippet link has been copied to your clipboard. 10 secs prior and after will be included for context.`,
      props: { isClosable: true },
    });
    trackAudioFeedbackEvents({
      eventName: 'Audio Feedback - Share Snippet: Copied URL To Clipboard',
      feedbackID: feedback.id,
    });
  };

  const onFlagSummary = () => {
    updateSummaryFlag({
      variables: {
        request: {
          userID,
          recordID: feedback.id,
          tenantID: tenantID,
          addFlag: SummaryFlag.Incorrect,
          keywordSummaryLocation: {
            msgIdx,
            keywordSummaryHash,
          },
        },
      },
    });
  };

  const isSummaryBeingPlayed = (metadata: SummaryMetadata | undefined) => {
    return isKeywordSummaryAudioBeingPlayed(metadata, currentPlayingMsgIndex);
  };

  if (feedback.content.type !== FeedbackContentType.Audio) {
    return null;
  }

  const { startTime, endTime } = getStartEndTimeFromAudioSummaryMetadata(
    summary.metadata,
    feedback.content.audioConversationEn,
  );

  const sourceSnippetURL = feedback.sourceV2
    ? getAudioSnippetSourceURL(
        feedback.sourceV2.externalLink,
        feedback.sourceV2.integrationChannelType,
        startTime,
        endTime,
        tenantName,
        tenantID,
      )
    : undefined;

  const getSnippetUrl = () =>
    getSnippetShareUrl({
      feedback,
      selectedLabels,
      startTime,
      endTime,
      tenantSlug,
    });

  const isAudioSourceEmpty = isEmptyAudioSourceFeedback(feedback);

  return (
    <AudioKeywordOrReasonSummaryActions
      onSnippetShare={() => onSummarySnippetShare(startTime, endTime)}
      onFlagSummary={onFlagSummary}
      isSummaryFlagged={isSummaryFlagged}
      isBeingPlayed={
        isAudioSourceEmpty ? false : isSummaryBeingPlayed(summary.metadata)
      }
      sourceSnippetURL={sourceSnippetURL}
      sourceName={feedback.sourceV2?.name ?? ''}
      feedbackID={feedback.id}
      summary={summary}
      labelID={labelID}
      getSnippetUrl={getSnippetUrl}
      isFlagLoading={isFlagLoading}
    />
  );
};
